/**
 * The lead and campaign vocabulary - the hand port of `LeadLimits` and `MarketingLimits` labels from
 * `Contracts/Leads.cs` and `Contracts/Marketing.cs`. The test asserts every generated enum member has
 * an entry, so a status appended server-side becomes a failing test rather than a blank label.
 */
import {
  CampaignRecipientOutcome,
  CampaignStatus,
  LeadActivityKind,
  LeadSource,
  LeadStatus
} from './contracts.gen.js';
import type { ChipTone } from './ui/Chip.js';

/** Pipeline order, left to right. */
export const LEAD_STATUSES: readonly LeadStatus[] = [
  LeadStatus.New,
  LeadStatus.Contacted,
  LeadStatus.Qualified,
  LeadStatus.Proposal,
  LeadStatus.Won,
  LeadStatus.Lost
];

export const LEAD_SOURCES: readonly LeadSource[] = [
  LeadSource.Manual,
  LeadSource.WebForm,
  LeadSource.Referral,
  LeadSource.Campaign,
  LeadSource.Event,
  LeadSource.ColdOutreach,
  LeadSource.Other
];

/** What a person can log by hand. */
export const LOGGABLE_ACTIVITIES: readonly LeadActivityKind[] = [
  LeadActivityKind.Call,
  LeadActivityKind.Email,
  LeadActivityKind.Meeting,
  LeadActivityKind.Note
];

const STATUS_LABELS: Record<LeadStatus, string> = {
  [LeadStatus.New]: 'New',
  [LeadStatus.Contacted]: 'Contacted',
  [LeadStatus.Qualified]: 'Qualified',
  [LeadStatus.Proposal]: 'Proposal sent',
  [LeadStatus.Won]: 'Won',
  [LeadStatus.Lost]: 'Lost'
};

const SOURCE_LABELS: Record<LeadSource, string> = {
  [LeadSource.Manual]: 'Entered by hand',
  [LeadSource.WebForm]: 'Website form',
  [LeadSource.Referral]: 'Referral',
  [LeadSource.Campaign]: 'Campaign',
  [LeadSource.Event]: 'Event',
  [LeadSource.ColdOutreach]: 'Cold outreach',
  [LeadSource.Other]: 'Other'
};

const ACTIVITY_LABELS: Record<LeadActivityKind, string> = {
  [LeadActivityKind.Note]: 'Note',
  [LeadActivityKind.Call]: 'Call',
  [LeadActivityKind.Email]: 'Email',
  [LeadActivityKind.Meeting]: 'Meeting',
  [LeadActivityKind.StatusChange]: 'Status change',
  [LeadActivityKind.Converted]: 'Converted',
  [LeadActivityKind.FormSubmitted]: 'Form submitted',
  [LeadActivityKind.CampaignSent]: 'Campaign sent',
  [LeadActivityKind.Unsubscribed]: 'Unsubscribed'
};

export const statusLabel = (s: LeadStatus): string => STATUS_LABELS[s] ?? `Status ${s}`;
export const sourceLabel = (s: LeadSource): string => SOURCE_LABELS[s] ?? `Source ${s}`;
export const activityLabel = (k: LeadActivityKind): string => ACTIVITY_LABELS[k] ?? `Activity ${k}`;

export function isOpen(status: LeadStatus): boolean {
  return status !== LeadStatus.Won && status !== LeadStatus.Lost;
}

export function statusTone(status: LeadStatus): ChipTone {
  switch (status) {
    case LeadStatus.Won:
      return 'ok';
    case LeadStatus.Lost:
      return 'danger';
    case LeadStatus.Proposal:
    case LeadStatus.Qualified:
      return 'brand';
    case LeadStatus.New:
      return 'info';
    default:
      return 'neutral';
  }
}

export function campaignTone(status: CampaignStatus): ChipTone {
  switch (status) {
    case CampaignStatus.Sent:
      return 'ok';
    case CampaignStatus.Sending:
      return 'brand';
    case CampaignStatus.Cancelled:
      return 'warn';
    default:
      return 'neutral';
  }
}

export function outcomeTone(outcome: CampaignRecipientOutcome): ChipTone {
  switch (outcome) {
    case CampaignRecipientOutcome.Sent:
      return 'ok';
    case CampaignRecipientOutcome.Failed:
      return 'danger';
    case CampaignRecipientOutcome.Skipped:
      return 'warn';
    default:
      return 'neutral';
  }
}

/** Splits a tag box ("msp, dental , ") into clean, de-duplicated tags. */
export function parseTags(text: string): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of text.split(/[,;]/)) {
    const tag = raw.trim();
    if (tag && !seen.has(tag.toLowerCase())) {
      seen.add(tag.toLowerCase());
      out.push(tag);
    }
  }
  return out;
}

/** "Follow up today", "Follow up 3 days ago", "Follow up 2026-10-28" - from a stored midday-UTC day. */
export function followUpLine(nextFollowUpUtc: string | null, today: string): string | null {
  if (!nextFollowUpUtc) return null;
  const day = nextFollowUpUtc.slice(0, 10);
  if (day === today) return 'Follow up today';
  const ms = Date.parse(today + 'T00:00:00Z') - Date.parse(day + 'T00:00:00Z');
  if (ms > 0) {
    const days = Math.round(ms / 86_400_000);
    return `Follow up was due ${days === 1 ? 'yesterday' : `${days} days ago`}`;
  }
  return `Follow up ${day}`;
}

/** The lead's display name: company, else contact, else its number. */
export function leadName(lead: { company: string | null; contactName: string | null; id: number }): string {
  return lead.company || lead.contactName || `Lead #${lead.id}`;
}
